import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, Sparkles, FileText, Music2, User, CornerDownRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * Apple-Spotlight-style AI omnisearch.
 * Opens centered, accepts a natural-language query, returns matching
 * articles/media/artists + a 2-3 sentence AI synthesis + 3 follow-ups.
 */
export default function SearchModal({ open, onClose }) {
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);
    const abortRef = useRef(null);

    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 50);
        } else {
            setQ("");
            setResult(null);
        }
    }, [open]);

    useEffect(() => {
        const onKey = (e) => {
            if (!open) return;
            if (e.key === "Escape") onClose?.();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const run = (query) => {
        if (abortRef.current) abortRef.current.abort();
        const ctl = new AbortController();
        abortRef.current = ctl;
        setLoading(true);
        api.post("/search/ai", { q: query }, { signal: ctl.signal })
            .then((r) => setResult(r.data))
            .catch((err) => {
                if (err?.code !== "ERR_CANCELED") setResult(null);
            })
            .finally(() => setLoading(false));
    };

    const onChange = (e) => {
        const v = e.target.value;
        setQ(v);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (v.trim().length < 2) {
            setResult(null);
            return;
        }
        debounceRef.current = setTimeout(() => run(v), 450);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (q.trim().length >= 2) run(q.trim());
    };

    if (!open) return null;

    return (
        <div
            data-testid="search-modal"
            className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="max-w-3xl mx-auto mt-[7vh] px-4"
                onClick={(e) => e.stopPropagation()}
            >
                <form
                    onSubmit={onSubmit}
                    className="flex items-center gap-3 bg-streetz-paper border border-streetz-line px-4 py-3"
                    data-testid="search-modal-form"
                >
                    {loading ? (
                        <Loader2 className="w-5 h-5 text-streetz-yellow animate-spin" />
                    ) : (
                        <Search className="w-5 h-5 text-streetz-muted" />
                    )}
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={onChange}
                        placeholder="Ask MyStreetz anything…  e.g. who sounds like Asake?"
                        data-testid="search-modal-input"
                        className="flex-1 bg-transparent outline-none text-white placeholder:text-streetz-muted text-lg"
                    />
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="search-modal-close"
                        aria-label="Close"
                        className="h-9 w-9 grid place-items-center text-streetz-muted hover:text-streetz-red"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </form>

                <div className="mt-4 bg-streetz-paper border border-streetz-line max-h-[70vh] overflow-y-auto">
                    {!result && !loading && (
                        <div className="p-8 text-center text-streetz-muted">
                            <Sparkles className="w-6 h-6 inline mb-2 text-streetz-yellow" />
                            <p>Type a question or a name. Hit return for instant results.</p>
                            <p className="text-xs mt-2 opacity-70">Powered by Claude AI · indexes articles, podcasts, playlists, interviews, videos, events, and approved artists.</p>
                        </div>
                    )}

                    {result && (
                        <div className="p-5 flex flex-col gap-6">
                            {result.ai_answer && (
                                <div data-testid="search-modal-ai-answer" className="bg-black border border-streetz-yellow/40 p-4">
                                    <div className="overline text-streetz-yellow flex items-center gap-2 mb-2">
                                        <Sparkles className="w-4 h-4" /> MYSTREETZ AI
                                    </div>
                                    <p className="text-white/90 leading-relaxed">{result.ai_answer}</p>
                                </div>
                            )}

                            {result.articles?.length > 0 && (
                                <Section title="ARTICLES" icon={<FileText className="w-4 h-4" />}>
                                    {result.articles.map((a) => (
                                        <Link
                                            key={a.slug}
                                            to={`/article/${a.slug}`}
                                            onClick={onClose}
                                            data-testid={`search-result-article-${a.slug}`}
                                            className="flex items-start gap-3 p-3 border border-streetz-line hover:border-streetz-yellow group"
                                        >
                                            {a.image_url && (
                                                <img src={a.image_url} alt="" className="w-16 h-16 object-cover shrink-0" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="overline text-[10px] text-streetz-red">{a.category}</div>
                                                <div className="font-display uppercase text-base leading-tight group-hover:text-streetz-yellow line-clamp-2">
                                                    {a.title}
                                                </div>
                                                <p className="text-xs text-streetz-muted mt-1 line-clamp-2">{a.excerpt}</p>
                                            </div>
                                        </Link>
                                    ))}
                                </Section>
                            )}

                            {result.media?.length > 0 && (
                                <Section title="MEDIA" icon={<Music2 className="w-4 h-4" />}>
                                    {result.media.map((m) => (
                                        <Link
                                            key={m.slug}
                                            to={`/m/${m.type}s`}
                                            onClick={onClose}
                                            data-testid={`search-result-media-${m.slug}`}
                                            className="flex items-start gap-3 p-3 border border-streetz-line hover:border-streetz-yellow group"
                                        >
                                            {m.image_url && (
                                                <img src={m.image_url} alt="" className="w-16 h-16 object-cover shrink-0" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="overline text-[10px] text-streetz-red">{m.type?.toUpperCase()}</div>
                                                <div className="font-display uppercase text-base leading-tight group-hover:text-streetz-yellow line-clamp-2">{m.title}</div>
                                                <p className="text-xs text-streetz-muted mt-1 line-clamp-2">{m.description}</p>
                                            </div>
                                        </Link>
                                    ))}
                                </Section>
                            )}

                            {result.artists?.length > 0 && (
                                <Section title="ARTISTS" icon={<User className="w-4 h-4" />}>
                                    {result.artists.map((ar) => (
                                        <Link
                                            key={ar.id}
                                            to={`/artist/${ar.id}`}
                                            onClick={onClose}
                                            data-testid={`search-result-artist-${ar.id}`}
                                            className="flex items-center gap-3 p-3 border border-streetz-line hover:border-streetz-yellow group"
                                        >
                                            {ar.image_url && (
                                                <img src={ar.image_url} alt="" className="w-12 h-12 object-cover rounded-full shrink-0" />
                                            )}
                                            <div className="min-w-0">
                                                <div className="font-display uppercase text-base group-hover:text-streetz-yellow line-clamp-1">{ar.artist_name}</div>
                                                <p className="text-xs text-streetz-muted line-clamp-1">{ar.genre}</p>
                                            </div>
                                        </Link>
                                    ))}
                                </Section>
                            )}

                            {result.follow_ups?.length > 0 && (
                                <Section title="TRY ASKING" icon={<CornerDownRight className="w-4 h-4" />}>
                                    <div className="flex flex-wrap gap-2">
                                        {result.follow_ups.map((f) => (
                                            <button
                                                key={f}
                                                onClick={() => { setQ(f); run(f); }}
                                                data-testid={`search-followup-${f.slice(0, 20)}`}
                                                className="overline text-[10px] bg-streetz-line text-white px-3 py-2 hover:bg-streetz-yellow hover:text-black"
                                            >
                                                {f}
                                            </button>
                                        ))}
                                    </div>
                                </Section>
                            )}

                            {result.articles.length === 0 && result.media.length === 0 && result.artists.length === 0 && (
                                <div className="text-center text-streetz-muted py-8">
                                    Nothing matches "{result.query}". Try a broader keyword.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Section({ title, icon, children }) {
    return (
        <div>
            <div className="overline text-streetz-yellow flex items-center gap-2 mb-2">
                {icon} {title}
            </div>
            <div className="flex flex-col gap-2">{children}</div>
        </div>
    );
}
